// src/helpers/contentfulHelper.js
import { createClient } from 'contentful';

const client = createClient({
  space: "us494go8jwv6",
  accessToken: "f174LlwX98UkkNizCl7RHnVLBMAcqOMwWZLCFHTl6oU",
});

export const fetchFooter = async () => {
  try {
    const entries = await client.getEntries({
      content_type: 'footer',
      limit: 1,
    });

    console.log('Footer entries:', entries);  

    return entries.items[0] || null;
  } catch (error) {
    console.error('Error fetching footer:', error);
    return null;
  }
};

export const fetchContactPage = async () => {
  try {
    const entries = await client.getEntries({
      content_type: 'contactPage',
      limit: 1,
    });

    console.log('Contact page entries:', entries);  

    return entries.items[0] || null;
  } catch (error) {
    console.error('Error fetching contact page:', error);
    return null;
  }
};

export const fetchServicesPage = async () => {
  try {
    const entries = await client.getEntries({
      content_type: 'servicesPage',
      limit: 1,
      include: 2,
    });
    console.log('Services page entries:', entries);
    return entries.items[0] || null;
  } catch (error) {
    console.error('Error fetching services page:', error);
    return null;
  }
};

export const fetchServiceBySlug = async (slug) => {
  try {
    const entries = await client.getEntries({
      content_type: 'service',
      'fields.slug': slug,
      limit: 1,
    });

    return entries.items[0] || null;
  } catch (error) {
    console.error('Error fetching service:', error);
    return null;
  }
};

export const fetchServiceByTitle = async (title) => {
  try {
    const entries = await client.getEntries({
      content_type: 'service',
      'fields.title': title,
      limit: 1,
    });

    return entries.items[0] || null;
  } catch (error) {
    console.error('Error fetching service by title:', error);
    return null;
  }
};

export const fetchGalleryPage = async () => {
  try {
    const res = await client.getEntries({
      content_type: 'galleryPage',
      limit: 1,
      include: 2,
    });

    return res.items[0];
  } catch (err) {
    console.error('Gallery page fetch failed', err);
    return null;
  }
};

export const fetchGalleryByTitle = async (title) => {
  try {
    const entries = await client.getEntries({
      content_type: 'gallery',
      'fields.title': title,
      limit: 1,
      include: 2,
    });

    const entry = entries.items[0] || null;
    if (!entry) return null;

    const imageLinks = entry.fields?.images || [];
    const includeAssets = entries.includes?.Asset || [];

    // Resolve linked assets from includes when SDK leaves link objects in place.
    if (
      Array.isArray(imageLinks) &&
      imageLinks.length > 0 &&
      imageLinks[0]?.sys?.linkType === 'Asset' &&
      !imageLinks[0]?.fields
    ) {
      const assetMap = new Map(includeAssets.map((asset) => [asset.sys.id, asset]));
      const resolvedImages = imageLinks
        .map((link) => assetMap.get(link?.sys?.id))
        .filter(Boolean);

      if (resolvedImages.length > 0) {
        entry.fields.images = resolvedImages;
      }
    }

    return entry;
  } catch (error) {
    console.error(`Error fetching gallery "${title}":`, error);
    return null;
  }
};

export const fetchAboutPage = async () => {
  try {
    const entries = await client.getEntries({
      content_type: 'aboutPage',
      limit: 1,
      include: 3,
    });

    const entry = entries.items[0] || null;
    if (!entry) return null;

    const includeEntries = entries.includes?.Entry || [];
    const entryMap = new Map(includeEntries.map((included) => [included.sys.id, included]));

    const maybeResolveEntryLink = (value) => {
      if (
        value &&
        value.sys?.type === 'Link' &&
        value.sys?.linkType === 'Entry' &&
        !value.fields
      ) {
        return entryMap.get(value.sys.id) || value;
      }
      return value;
    };

    // Resolve single CTA entry link if needed.
    if (entry.fields?.cta) entry.fields.cta = maybeResolveEntryLink(entry.fields.cta);
    if (entry.fields?.ctaBlock) entry.fields.ctaBlock = maybeResolveEntryLink(entry.fields.ctaBlock);
    if (entry.fields?.callToAction) {
      entry.fields.callToAction = maybeResolveEntryLink(entry.fields.callToAction);
    }

    // Resolve arrays of entry links (e.g. values/gallery refs) if needed.
    ['values', 'gallery', 'galleries', 'photos', 'images'].forEach((fieldId) => {
      const fieldValue = entry.fields?.[fieldId];
      if (!Array.isArray(fieldValue)) return;

      entry.fields[fieldId] = fieldValue.map((item) => maybeResolveEntryLink(item));
    });

    return entry;
  } catch (error) {
    console.error('Error fetching about page:', error);
    return null;
  }
};

export const fetchOurTeamPage = async () => {
  try {
    const res = await client.getEntries({
      content_type: "ourTeam",
      limit: 1,
      include: 2, // pulls in linked teamMember entries + images
    });

    console.log("Our Team page fetch result:", res);

    return res.items[0] ?? null;
  } catch (err) {
    console.error("Our Team page fetch failed", err);
    return null;
  }
};

export const fetchHomePage = async () => {
  try {
    const entries = await client.getEntries({
      content_type: "homePage",
      limit: 1,
      include: 3,
    });

    const entry = entries.items[0] || null;
    if (!entry) return null;

    const includedEntries = entries.includes?.Entry || [];
    const includedAssets = entries.includes?.Asset || [];
    const entryMap = new Map(includedEntries.map((item) => [item.sys.id, item]));
    const assetMap = new Map(includedAssets.map((item) => [item.sys.id, item]));

    const resolveEntryLink = (value) => {
      if (
        value &&
        value.sys?.type === "Link" &&
        value.sys?.linkType === "Entry" &&
        !value.fields
      ) {
        return entryMap.get(value.sys.id) || value;
      }
      return value;
    };

    const resolveAssetLink = (value) => {
      if (
        value &&
        value.sys?.type === "Link" &&
        value.sys?.linkType === "Asset" &&
        !value.fields
      ) {
        return assetMap.get(value.sys.id) || value;
      }
      return value;
    };

    if (Array.isArray(entry.fields?.featureTiles)) {
      entry.fields.featureTiles = entry.fields.featureTiles
        .map((tile) => resolveEntryLink(tile))
        .filter(Boolean)
        .map((tile) => {
          if (tile?.fields?.image) {
            tile.fields.image = resolveAssetLink(tile.fields.image);
          }
          return tile;
        });
    }

    return entry;
  } catch (error) {
    console.error("Error fetching home page:", error);
    return null;
  }
};

export const fetchSimplePageByInternalTitle = async (internalTitle) => {
  try {
    const entries = await client.getEntries({
      content_type: 'simplePage',
      'fields.internalTitle': internalTitle,
      limit: 1,
      include: 3,
    });

    return entries.items[0] || null;
  } catch (error) {
    console.error(`Error fetching simple page "${internalTitle}":`, error);
    return null;
  }
};

export const fetchSiteSettings = async () => {
  try {
    const entries = await client.getEntries({
      content_type: 'siteSettings',
      limit: 1,
    });

    return entries.items[0] || null;
  } catch (error) {
    console.error('Error fetching site settings:', error);
    return null;
  }
};

/* ===============================
   Map Our Team page entry
================================ */
export const mapOurTeamPage = (entry) => {
  if (!entry) return null;

  const { title, introCopy, teamMember } = entry.fields;

  return {
    title,
    introCopy, // Contentful Rich Text document
    teamMembers: (teamMember || []).map((member) => {
      const imageFile = member.fields.image?.fields?.file;
      const memberName = member.fields.name || '';

      return {
        id: member.sys.id,
        name: memberName,
        position: member.fields.position || '',
        image: imageFile ? `https:${imageFile.url}` : null,
        imageAlt:
          member.fields.image?.fields?.title || 'Team member',
      };
    }),
  };
};
